import { getSureLockRequestInstance } from '@/plugins/axios';
import {
  PreservationsResponse,
  DownloadResponse,
  ProcessedRecordsResponse,
  UpdatePreservationResponse,
  CustomerInfoResponse,
  CreatePreservationResponse,
  ZipsResponse,
} from '@/types/surelockResponses';
import { GET_PRESERVATIONS_LIMIT, PRESERVATION_UPDATE_STATUSES } from '@/utils/constants';
import _ from 'lodash';

const request = getSureLockRequestInstance();

async function getCustomerInfo(uid: string, sid: string): Promise<CustomerInfoResponse> {
  const { data } = await request.get(`/v1/customer/${uid}/${sid}`)
  return data;
}

async function getPreservationRecords(): Promise<PreservationsResponse> {
  const { data } = await request.get(`/v1/preserve?status=PROCESSING,SUCCESSFUL,ERROR,CLOSED,DELETED&limit=${GET_PRESERVATIONS_LIMIT}`);
  return data;
}

async function makeGetRequest(url: string): Promise<PreservationsResponse> {
  if (!url) {
    throw new Error('makeGetRequest: expected a non-empty URL');
  }
  const isAbsoluteUrl = /^[a-z][a-z\d+\-.]*:\/\//i.test(url);
  const normalizedUrl = (url.startsWith('/') || isAbsoluteUrl) ? url : `/${url}`;
  const { data } = await request.get(normalizedUrl);
  return data;
}

async function getPreservationById(preservationId: string): Promise<any> {
  const { data } = await request.get(`/v1/preserve/${preservationId}`);
  return data;
}

export async function getProcessedRecords(preservationId: string): Promise<ProcessedRecordsResponse> {
  const { data } = await request.get(`/v1/preserve/${preservationId}/videos`);
  return data;
}

export async function getZippedFiles(preservationId: string): Promise<ZipsResponse> {
  const { data } = await request.get(`/v1/preserve/${preservationId}/zips`);
  return data;
}

async function createPreservation(uid: string, sid: string, start: number, end: number, uuids: Array<string>, description: string): Promise<CreatePreservationResponse> {
  const { data } = await request.post(`/v1/preserve/${uid}/${sid}/time/${start}/${end}`, { uuids, description });
  return data;
}

export async function compressPreservation(preservationId: string): Promise<void> {
  await request.post(`/v1/compress/${preservationId}`);
}

export async function downloadPreservation(preservationId: string, zipFileName: string): Promise<DownloadResponse> {
  const response = await request.get(`/v1/download/${preservationId}?fileName=${zipFileName}`, { responseType: 'blob' });

  return {
    zipFile: response.data,
  };
}

async function updatePreservation(preservationId: string, description: string, status?: string, sent?: boolean): Promise<UpdatePreservationResponse> {
  const body: any = { description };
  if (status && PRESERVATION_UPDATE_STATUSES.includes(status)) {
    body.status = status;
  }

  if (!_.isNil(sent)) {
    body.sent = sent;
  }

  const { data } = await request.patch(`/v1/preserve/${preservationId}`, body);
  return data;
}

export default {
  getCustomerInfo,
  getPreservationRecords,
  makeGetRequest,
  getPreservationById,
  getProcessedRecords,
  getZippedFiles,
  createPreservation,
  compressPreservation,
  downloadPreservation,
  updatePreservation,
}
